import { type GridRowId } from "@mui/x-data-grid-pro";

/**
 * 絞り込みが参照する最小の行形。
 * propertyCode 等には依存しないため、他の一括取得系画面の行でも流用できる
 */
export interface BulkFilterableRow {
  id: GridRowId;
  prefectureName: string;
  location: string;
  companyName: string;
  realEstateType: string;
}

// 一括取得画面のクライアント側絞り込み（DB検索ではなく受け渡しリストが対象）

// companyName は「A社、B社」のように連結されている場合があるため分解して比べる。
// 空文字は絞り込みの選択肢に空欄として並んでしまうため落とす
export const splitCompanyNames = (companyName: string): string[] =>
  companyName.split("、").filter(Boolean);

/** 絞り込み条件。null は「指定なし」 */
export interface BulkListFilter {
  prefecture: string | null;
  city: string | null;
  /** 選択された会社名。いずれかに一致する行を残す（「A社、B社」の連結表示は分解して照合） */
  companyNames: string[] | null;
  /** 暫定: 流用元はDB保有筆数だが、ここではリスト内の会社ごとの行数に読み替えている */
  holdingCountMin: number | null;
  holdingCountMax: number | null;
  /** 照合結果の realEstateType（土地/建物/区分建物）。いずれかに一致する行を残す */
  realEstateTypes: string[] | null;
  /**
   * 事項種別で選ばれたグリッドのチェック列（field名）。条件を変えた時点で
   * いずれかの列にチェックがある行だけを残す
   */
  bookTypeFields: string[] | null;
}

export const EMPTY_BULK_LIST_FILTER: BulkListFilter = {
  prefecture: null,
  city: null,
  companyNames: null,
  holdingCountMin: null,
  holdingCountMax: null,
  realEstateTypes: null,
  bookTypeFields: null,
};

/**
 * 絞り込み条件を AND 適用して残る行を返す。
 * - 都道府県: prefectureName の一致
 * - 市区町村: location の前方一致
 * - 会社名: 選択された会社のいずれかと完全一致（「A社、B社」の連結表示は分解して照合）
 * - 保有物件数: 連結表示を分解した各会社のリスト内行数のいずれかがレンジ内
 * - 土地・建物: realEstateType がいずれかに一致
 * - 事項種別: bookTypeFields のいずれかの列で checkedIdsByField にチェックがある行
 */
export const filterBulkPropertyRows = <T extends BulkFilterableRow>(
  rows: T[],
  filter: BulkListFilter,
  checkedIdsByField: Record<string, Set<GridRowId>> = {}
): T[] => {
  // 「A社、B社」の連結行も分解して各社の保有数に計上する（会社名照合と同じ単位で数える）
  const rowCountByCompany = new Map<string, number>();
  for (const row of rows) {
    for (const name of splitCompanyNames(row.companyName)) {
      rowCountByCompany.set(name, (rowCountByCompany.get(name) ?? 0) + 1);
    }
  }

  return rows.filter((row) => {
    if (filter.prefecture != null && row.prefectureName !== filter.prefecture) {
      return false;
    }
    if (filter.city != null && !row.location.startsWith(filter.city)) {
      return false;
    }
    if (filter.companyNames != null) {
      const rowCompanyNames = splitCompanyNames(row.companyName);
      if (!filter.companyNames.some((name) => rowCompanyNames.includes(name))) {
        return false;
      }
    }
    if (filter.holdingCountMin != null || filter.holdingCountMax != null) {
      const names = splitCompanyNames(row.companyName);
      const counts =
        names.length > 0
          ? names.map((name) => rowCountByCompany.get(name) ?? 0)
          : [0];
      const inRange = counts.some(
        (count) =>
          (filter.holdingCountMin == null || count >= filter.holdingCountMin) &&
          (filter.holdingCountMax == null || count <= filter.holdingCountMax)
      );
      if (!inRange) {
        return false;
      }
    }
    if (
      filter.realEstateTypes != null &&
      !filter.realEstateTypes.includes(row.realEstateType)
    ) {
      return false;
    }
    if (
      filter.bookTypeFields != null &&
      !filter.bookTypeFields.some((field) =>
        checkedIdsByField[field]?.has(row.id)
      )
    ) {
      return false;
    }
    return true;
  });
};
